import { useEffect, useState } from 'react';
import { api, type Me, type QaFinding } from '../lib/api';

const SEV_RANK: Record<QaFinding['severity'], number> = { info: 0, warn: 1, error: 2, critical: 3 };

function sevPill(sev: QaFinding['severity']) {
  if (sev === 'critical') return <span className="pill-err">Critical</span>;
  if (sev === 'error')    return <span className="pill-err">Error</span>;
  if (sev === 'warn')     return <span className="pill-warn">Warn</span>;
  return <span className="pill-ok">Info</span>;
}

function catBadge(cat: QaFinding['category']) {
  const map: Record<QaFinding['category'], string> = {
    bug: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
    error: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
    glitch: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
    performance: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
  };
  return <span className={`pill ${map[cat]}`}>{cat}</span>;
}

function timeAgo(iso: string) {
  const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function Qa({ me }: { me: Me }) {
  const [findings, setFindings] = useState<QaFinding[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [minSeverity, setMinSeverity] = useState<QaFinding['severity']>('info');
  const [audit, setAudit] = useState<Awaited<ReturnType<typeof api.runFrontendAudit>> | null>(null);
  const [auditBusy, setAuditBusy] = useState(false);

  async function runWalkthrough() {
    setAuditBusy(true);
    setErr(null);
    try {
      const r = await api.runFrontendAudit();
      setAudit(r);
      await load(); // findings update from the same sweep
    } catch (e: any) {
      setErr(e.message);
    } finally { setAuditBusy(false); }
  }

  async function load() {
    try {
      setErr(null);
      const r = await api.qaFindings({ limit: 50, minSeverity });
      setFindings(r.findings);
    } catch (e: any) {
      setErr(e.message);
    }
  }

  async function analyzeNow() {
    setBusy(true);
    try {
      await api.qaAnalyzeNow();
      await load();
    } catch (e: any) {
      setErr(e.message);
    } finally { setBusy(false); }
  }

  async function resolve(id: string) {
    try {
      await api.qaResolve(id);
      setFindings((cur) => cur?.filter((f) => f.finding_id !== id) ?? null);
    } catch (e: any) { setErr(e.message); }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [minSeverity]);

  const visible = findings ?? [];
  const bySev = { critical: 0, error: 0, warn: 0, info: 0 } as Record<QaFinding['severity'], number>;
  for (const f of visible) bySev[f.severity]++;

  const canResolve = me.role === 'super_admin' || me.role === 'tenant_admin';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">QA Sentry</h1>
          <p className="text-gray-500 text-sm mt-1">
            AI-curated bug, error, glitch, and performance findings from the radar QA loop. Cron runs every 5 minutes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runWalkthrough}
            disabled={auditBusy}
            className="btn-secondary"
            title="RADAR walks every module as an end user and matches the front end against the backend"
          >
            {auditBusy ? 'Walking modules…' : 'Run walkthrough'}
          </button>
          <button
            onClick={analyzeNow}
            disabled={busy}
            className="btn-primary"
          >
            {busy ? 'Analyzing…' : 'Re-run now'}
          </button>
        </div>
      </div>

      {audit && (
        <section className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold tracking-tight">
              End-user walkthrough —{' '}
              <span className={audit.overall === 'pass' ? 'text-tnh-ok' : audit.overall === 'warn' ? 'text-tnh-warn' : 'text-tnh-err'}>
                {audit.overall.toUpperCase()}
              </span>
            </h2>
            <span className="text-xs text-gray-500">
              {audit.total_fails} fail · {audit.total_warns} warn · {audit.tenants_walked} tenant(s)
            </span>
          </div>
          <div className="space-y-3">
            {audit.results.map((t) => (
              <div key={t.tenant}>
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t.tenant}</div>
                <div className="space-y-1">
                  {t.checks.map((c) => (
                    <div key={c.key} className="flex items-center gap-2 text-sm">
                      <span className={
                        c.status === 'PASS' ? 'pill-ok' : c.status === 'WARN' ? 'pill-warn' : 'pill-err'
                      }>{c.status}</span>
                      <span className="font-medium min-w-[150px]">{c.label}</span>
                      <span className="text-gray-500">{c.detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <div className="card p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Critical</div>
          <div className="text-2xl font-semibold text-tnh-err mt-1">{bySev.critical}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Errors</div>
          <div className="text-2xl font-semibold text-tnh-err mt-1">{bySev.error}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Warnings</div>
          <div className="text-2xl font-semibold text-tnh-warn mt-1">{bySev.warn}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Info</div>
          <div className="text-2xl font-semibold text-gray-600 dark:text-gray-300 mt-1">{bySev.info}</div>
        </div>
      </section>

      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">Min severity:</span>
        {(['info', 'warn', 'error', 'critical'] as QaFinding['severity'][]).map((s) => (
          <button
            key={s}
            onClick={() => setMinSeverity(s)}
            className={`px-3 py-1 rounded-md text-xs ${
              minSeverity === s
                ? 'bg-tnh-accent text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {err && <div className="card p-4 text-tnh-err text-sm">{err}</div>}

      <div className="space-y-3">
        {findings === null && <div className="card p-8 text-center text-gray-500">Loading findings…</div>}
        {findings && findings.length === 0 && (
          <div className="card p-8 text-center text-gray-500">
            No open findings. The radar reports everything is operational.
          </div>
        )}
        {visible
          .slice()
          .sort((a, b) => SEV_RANK[b.severity] - SEV_RANK[a.severity] || b.detected_at.localeCompare(a.detected_at))
          .map((f) => (
            <div key={f.finding_id} className="card p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {sevPill(f.severity)}
                    {catBadge(f.category)}
                    <span className="text-xs text-gray-500">{f.affected_service}</span>
                    <span className="text-xs text-gray-400">· {timeAgo(f.detected_at)}</span>
                  </div>
                  <div className="font-medium mt-2">{f.title}</div>
                  <div className="text-sm text-gray-500 mt-1">{f.description}</div>

                  {(f.ai_summary || f.ai_root_cause || f.ai_suggestion) && (
                    <div className="mt-3 p-3 rounded-lg bg-tnh-accent/5 dark:bg-tnh-accent/10 border border-tnh-accent/20 space-y-1.5 text-sm">
                      <div className="text-[10px] uppercase tracking-wider text-tnh-accent font-semibold">AI analysis</div>
                      {f.ai_summary &&     <div><span className="font-medium">Summary:</span> {f.ai_summary}</div>}
                      {f.ai_root_cause &&  <div><span className="font-medium">Root cause:</span> {f.ai_root_cause}</div>}
                      {f.ai_suggestion &&  <div><span className="font-medium">Suggested action:</span> {f.ai_suggestion}</div>}
                    </div>
                  )}

                  {(f.evidence as any)?.image_key && (
                    <a href={api.qaUiImageUrl((f.evidence as any).image_key)} target="_blank" rel="noreferrer" className="block mt-2">
                      <img src={api.qaUiImageUrl((f.evidence as any).image_key)} alt="captured screenshot"
                           className="max-h-56 rounded-lg border border-tnh-border dark:border-tnh-border-dark" />
                    </a>
                  )}
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer text-gray-500">Evidence</summary>
                    <pre className="mt-1 p-2 rounded bg-gray-50 dark:bg-gray-900 text-[11px] overflow-x-auto">
                      {JSON.stringify(f.evidence, null, 2)}
                    </pre>
                  </details>
                </div>
                {canResolve && (
                  <button onClick={() => resolve(f.finding_id)} className="btn-ghost text-xs whitespace-nowrap">
                    Resolve
                  </button>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
